import type {
  ReviewBlocker,
  ReviewWarning,
  SchedulePublishReview,
} from "../data/schedule-publish-review";
import { PUBLISH_BLOCK_REASONS, type PublishBlock, type WizardStep } from "./schedule-wizard-model";

/**
 * The one publish review gate (lock O-4).
 *
 * Both Schedule surfaces that can publish a week (the connected wizard's Review step and the
 * Builder's publish dialog) ask this module the same two questions: what does this week
 * actually create, and what is the single reason it cannot run. Before this, each surface
 * computed its own answer from its own client-side mirror of the expansion, which is how two
 * screens end up disagreeing about the same week.
 *
 * The counts here are the host's. `createSeatCount` is a physical obligation seat count from
 * the service record, so this module never multiplies services by headcount to produce a
 * number an operator might read as proof.
 *
 * DISPLAY GATE ONLY. The host enforces permission, the Monday anchor, the overlap rule, and
 * the reviewed revision, and refuses regardless of what this returns. A null block here is
 * never authorization.
 */

/**
 * Sentences for the two host blockers the shipped Console copy had no line for.
 *
 * `pattern_not_chosen` exists because the review read answers for a null pattern rather than
 * erroring, so the zero state is one honest read instead of a special case.
 *
 * `officer_double_booked` is the O-1 rule. It names the situation and the next move without
 * naming the officer or the site: the host answers this as a boolean precisely so a blocker
 * cannot become a roster oracle for whoever is looking at the screen.
 */
export const HOST_BLOCK_REASONS: Readonly<Record<"pattern_not_chosen" | "officer_double_booked", string>> = {
  pattern_not_chosen: "Choose a shift pattern to review",
  officer_double_booked: "An officer is double booked in this week. Fix the overlap, then publish.",
};

/** Host blocker to the locked Console sentence, reusing shipped copy wherever one exists. */
const BLOCKER_COPY: Readonly<Record<ReviewBlocker, string>> = {
  pattern_not_chosen: HOST_BLOCK_REASONS.pattern_not_chosen,
  pattern_retired: PUBLISH_BLOCK_REASONS.retired,
  pattern_not_publishable: PUBLISH_BLOCK_REASONS["not-publishable"],
  nothing_to_create: PUBLISH_BLOCK_REASONS["nothing-new"],
  officer_double_booked: HOST_BLOCK_REASONS.officer_double_booked,
};

/**
 * Where the operator goes to fix each blocker.
 *
 * A retired or unrecognized pattern has no repair inside this flow, and neither does an
 * overlap: the fix is in the week's assignments, which is a different surface and a different
 * permission. Offering a ghost button that only looks like a fix is the defect the flow lock
 * bans, so those return null and the sentence stands on its own.
 */
const BLOCKER_REPAIR: Readonly<Record<ReviewBlocker, { label: string | null; step: WizardStep | null }>> = {
  pattern_not_chosen: { label: "Edit shift", step: "define" },
  pattern_retired: { label: null, step: null },
  pattern_not_publishable: { label: null, step: null },
  nothing_to_create: { label: "Choose week", step: "week" },
  officer_double_booked: { label: null, step: null },
};

export interface SchedulePublishGateInput {
  /**
   * Display permission. The operator sees the reason rather than a control that cannot run.
   */
  readonly canManageSchedule: boolean;
  /** The host review, or null while the read is in flight or after it failed. */
  readonly review: SchedulePublishReview | null;
  /**
   * True while the review read is in flight. A missing review is not "nothing to create":
   * that would be a confident sentence about a week nobody has checked yet.
   */
  readonly reviewLoading?: boolean;
  /** The read failed. The gate stays closed and the surface offers the retry. */
  readonly reviewError?: string | null;
  /**
   * False when the shape on screen has never been written, or was edited since the last
   * write. The host can only review what it stored, so an unwritten shape cannot be
   * previewed honestly. Omitted means saved: only the wizard's own state can say otherwise.
   */
  readonly draftSaved?: boolean;
}

export interface SchedulePublishGate {
  /** The single reason publish cannot run, or null when it genuinely can. */
  readonly block: PublishBlock | null;
  /** True only when a primary-styled publish control is honest to render. */
  readonly canPublish: boolean;
  /** Services this publish would create. Always the host's number. */
  readonly createSlotCount: number;
  /** Physical obligation seats those services carry. Never services times headcount. */
  readonly createSeatCount: number;
  /** "Creates 5 shifts and 10 seats", or the honest zero line. */
  readonly countLine: string;
  /**
   * D-02 permanence line when this review would create seats, else null (loading, zero create,
   * or already published). Rendered beside the count so publish cannot run without it.
   */
  readonly consequenceLine: string | null;
  /** Fact lines that do not block, in render order. */
  readonly warningLines: readonly string[];
  /** The revision to hand the publish act, or null when there is nothing safe to publish. */
  readonly reviewRevision: string | null;
}

/** Loading and failure copy, so both surfaces say the same thing about the same state. */
export const REVIEW_LOADING_LINE = "Checking this week...";
export const REVIEW_UNAVAILABLE_REASON = "This week could not be reviewed, so publish cannot run";

/**
 * D-14 refusal for missing manage permission or an active support session. The support banner
 * (lane 9) is the only place that explains why; this sentence stays generic on purpose.
 */
export const SCHEDULE_WRITE_REFUSED =
  "Schedule changes are unavailable right now. Check your access or try again.";

/**
 * D-02 permanence sentence. The seat count is the host's physical obligation count; the second
 * sentence is locked verbatim so an operator cannot publish without reading that seats stay.
 */
export function buildPublishConsequenceLine(seatCount: number): string {
  const n = Math.max(0, Math.floor(seatCount));
  const seats = `${n} ${n === 1 ? "seat" : "seats"}`;
  return `Publishing creates ${seats} for this week. Seats, their times, and their route labels become part of the service record and stay there.`;
}

function countLine(review: SchedulePublishReview | null): string {
  if (review === null) {
    return REVIEW_LOADING_LINE;
  }
  const { createSlotCount: slots, createSeatCount: seats } = review;
  if (slots === 0) {
    // Says what exists rather than only what is missing, so a fully published week reads as
    // finished rather than broken.
    return review.existingSlotCount > 0
      ? `Creates nothing new · ${review.existingSlotCount} ${review.existingSlotCount === 1 ? "shift" : "shifts"} already published for this week`
      : "Creates 0 shifts";
  }
  const shiftPart = `${slots} ${slots === 1 ? "shift" : "shifts"}`;
  // Always name seats when the host returned any: headcount one still creates obligation seats,
  // and D-02 requires the operator to see that number before publish.
  const seatPart = seats > 0 ? ` · ${seats} ${seats === 1 ? "seat" : "seats"} to fill` : "";
  return `Creates ${shiftPart}${seatPart}`;
}

function warningLine(warning: ReviewWarning): string | null {
  if (warning.count <= 0) {
    return null;
  }
  const seats = `${warning.count} ${warning.count === 1 ? "seat" : "seats"}`;
  switch (warning.code) {
    case "uncovered_seat":
      // A fact about the week, not about this publish: it can be true before the operator
      // arrives and is still true after a successful publish.
      return `${seats} already published for this week have nobody assigned yet.`;
    case "approval_required_seat":
      return `Officers can request these shifts in Guard. A supervisor approves requests, so the ${seats} are not filled by publishing.`;
    default:
      return null;
  }
}

/**
 * Resolve the gate. Order is the lock's priority order, and exactly one reason speaks:
 *
 *   1. permission, because nothing downstream can run without it
 *   2. an unwritten shape, because every fact below it would describe a row that does not exist
 *   3. a review this surface could not read, because silence is safer than a guess
 *   4. the host's own ordered blockers, first one only
 */
export function resolveSchedulePublishGate(input: SchedulePublishGateInput): SchedulePublishGate {
  const review = input.review;
  const createSeatCount = review?.createSeatCount ?? 0;
  const createSlotCount = review?.createSlotCount ?? 0;
  const base = {
    createSlotCount,
    createSeatCount,
    countLine: countLine(review),
    consequenceLine:
      review !== null && createSlotCount > 0 && createSeatCount > 0
        ? buildPublishConsequenceLine(createSeatCount)
        : null,
    warningLines: (review?.warnings ?? [])
      .map(warningLine)
      .filter((line): line is string => line !== null),
  };
  const blocked = (block: PublishBlock): SchedulePublishGate => ({
    ...base,
    block,
    canPublish: false,
    reviewRevision: null,
  });

  if (!input.canManageSchedule) {
    return blocked({
      kind: "no-permission",
      reason: SCHEDULE_WRITE_REFUSED,
      repairLabel: null,
      repairStep: null,
    });
  }
  if (input.draftSaved === false) {
    return blocked({
      kind: "unsaved",
      reason: PUBLISH_BLOCK_REASONS.unsaved,
      repairLabel: "Edit shift",
      repairStep: "define",
    });
  }
  if (review === null) {
    // Loading and failed are both "not reviewable right now", and both must keep the
    // primary control off. They differ only in what the surface says next to it.
    return blocked({
      kind: "not-publishable",
      reason: input.reviewLoading === true ? REVIEW_LOADING_LINE : REVIEW_UNAVAILABLE_REASON,
      repairLabel: null,
      repairStep: null,
    });
  }
  const first = review.blockers[0];
  if (first !== undefined) {
    const repair = BLOCKER_REPAIR[first];
    return blocked({
      kind: hostBlockerKind(first),
      reason: BLOCKER_COPY[first],
      repairLabel: repair.label,
      repairStep: repair.step,
    });
  }
  return { ...base, block: null, canPublish: true, reviewRevision: review.reviewRevision };
}

/**
 * The Console block kind a host blocker reports as, so the stepper and the existing styling
 * hooks keep working without a second vocabulary. `officer_double_booked` has no shipped kind
 * and reports as not-publishable, which is exactly what it is for this week.
 */
function hostBlockerKind(blocker: ReviewBlocker): PublishBlock["kind"] {
  switch (blocker) {
    case "pattern_not_chosen":
      return "unsaved";
    case "pattern_retired":
      return "retired";
    case "nothing_to_create":
      return "nothing-new";
    case "pattern_not_publishable":
    case "officer_double_booked":
    default:
      return "not-publishable";
  }
}

/**
 * Success copy from the publish receipt, never from the review.
 *
 * Both counts are stated because they are different facts: the services that landed on the
 * calendar, and the physical obligation seats the service record now holds for them. A
 * duplicate submission created nothing and says so rather than repeating a green number the
 * operator already earned once.
 */
export function buildPublishReceiptLine(receipt: {
  readonly createdSlotCount: number;
  readonly createdSeatCount: number;
  readonly weekSlotCount: number;
}): string {
  const slots = receipt.createdSlotCount;
  if (slots === 0) {
    const week = receipt.weekSlotCount;
    return `Already published · ${week} ${week === 1 ? "shift is" : "shifts are"} on the calendar for this week`;
  }
  const seats = receipt.createdSeatCount;
  const shiftPart = `${slots} ${slots === 1 ? "shift is" : "shifts are"} on the calendar`;
  const seatPart = seats > 0 ? ` · ${seats} ${seats === 1 ? "seat" : "seats"} to fill` : "";
  return `Published · ${shiftPart}${seatPart}`;
}
